import { all, put, takeEvery, select, PutEffect, SelectEffect } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { getItemsAsync, newItemAsync, saveItemAsync, cancelItemAsync, editItemAsync, deleteItemAsync, loadDependenciesAsync } from './actions';
import { getItemsAsync as loadAccommodationsAsync, getAreAccommodationsLoaded } from '../accommodations';

import apiClient from '../../api/apiClientInstance';
import { PriceListModel } from '../../api/interfaces';
import { BaseDataResponse } from '../../api/responses';
import { navigateTo, navigateToDetail, NavigateToDetail, ROUTES } from '../../navigation';
import { getAreItemsLoaded } from './selectors';
import {
  GetAllRequestAsyncActionPayload,
  isDataReloadRequired,
  registerSaveEntityRequest,
  registerCancelEntityRequest,
  registerDeleteEntityRequest
} from '../../core';

export default function* rootSaga(): any {
  yield all([
    yield takeEvery(getItemsAsync.request, getPriceListsRequest),
    yield takeEvery(newItemAsync.request, newPriceListRequest),
    yield takeEvery(saveItemAsync.request, savePriceListRequest),
    yield takeEvery(cancelItemAsync.request, cancelPriceListRequest),
    yield takeEvery(editItemAsync.request, editPriceListRequest),
    yield takeEvery(deleteItemAsync.request, deletePriceListRequest),
    yield takeEvery(loadDependenciesAsync.request, loadDependenciesRequest)
  ]);
}

type GetPriceListsRequestGeneratorType =
  | SelectEffect
  | Generator<SelectEffect, boolean, boolean>
  | Promise<BaseDataResponse<PriceListModel[]>>
  | PutEffect<ActionType<typeof getItemsAsync.cancel>>
  | PutEffect<ActionType<typeof getItemsAsync.success>>
  | PutEffect<ActionType<typeof getItemsAsync.failure>>;

function* getPriceListsRequest(
  action: ReturnType<typeof getItemsAsync.request>
): Generator<GetPriceListsRequestGeneratorType, void, boolean & BaseDataResponse<PriceListModel[]>> {
  try {
    console.debug('getPriceListsRequest');

    const payload = action.payload as GetAllRequestAsyncActionPayload;
    const isReloadRequired = yield isDataReloadRequired(getAreItemsLoaded, payload);
    if (!isReloadRequired) {
      yield put(getItemsAsync.cancel());
      return;
    }

    const response: BaseDataResponse<PriceListModel[]> = yield apiClient.apiCallHandler({
      context: apiClient.PriceLists,
      apiCallFnName: 'getAllAsync'
    });

    if (response.resultCode === 0) {
      yield put(getItemsAsync.success(response.data));
    } else {
      yield put(getItemsAsync.failure(new Error(JSON.stringify({ reason: response.resultReason, code: response.resultCode }))));
    }
  } catch (error) {
    yield put(getItemsAsync.failure(error));
  }
}

type NewPriceListRequestGeneratorType =
  | PutEffect<ActionType<typeof navigateTo>>
  | PutEffect<ActionType<typeof newItemAsync.success>>
  | PutEffect<ActionType<typeof newItemAsync.failure>>;

function* newPriceListRequest(action: ReturnType<typeof newItemAsync.request>): Generator<NewPriceListRequestGeneratorType, void, void> {
  try {
    console.debug('newPriceListRequest');

    yield put(navigateTo(ROUTES.SETTINGS_PRICE_LISTS_CREATE));
    yield put(newItemAsync.success());
  } catch (error) {
    yield put(newItemAsync.failure(error));
  }
}

type EditPriceListRequestGeneratorType =
  | Promise<BaseDataResponse<PriceListModel>>
  | PutEffect<ActionType<typeof editItemAsync.success>>
  | PutEffect<ActionType<typeof editItemAsync.failure>>
  | PutEffect<ActionType<typeof navigateToDetail>>
  | PutEffect<ActionType<typeof navigateTo>>;

function* editPriceListRequest(
  action: ReturnType<typeof editItemAsync.request>
): Generator<EditPriceListRequestGeneratorType, void, BaseDataResponse<PriceListModel>> {
  try {
    console.debug('editPriceListRequest');
    const itemToEditId = action.payload;

    yield put(navigateToDetail({ masterPage: ROUTES.SETTINGS_PRICE_LISTS_DETAIL, detailId: itemToEditId } as NavigateToDetail));
    yield put(editItemAsync.success());
  } catch (error) {
    yield put(editItemAsync.failure(error));
  }
}

const deletePriceListRequest = registerDeleteEntityRequest(
  deleteItemAsync.success,
  deleteItemAsync.failure,
  apiClient.PriceLists,
  ROUTES.SETTINGS_PRICE_LISTS
);

const savePriceListRequest = registerSaveEntityRequest<PriceListModel>(
  saveItemAsync.success,
  saveItemAsync.failure,
  apiClient.PriceLists,
  ROUTES.SETTINGS_PRICE_LISTS
);

const cancelPriceListRequest = registerCancelEntityRequest(cancelItemAsync.success, cancelItemAsync.failure, ROUTES.SETTINGS_PRICE_LISTS);

type LoadDependenciesRequestGeneratorType =
  | SelectEffect
  | PutEffect<ActionType<typeof loadAccommodationsAsync.request>>
  | PutEffect<ActionType<typeof getItemsAsync.request>>
  | PutEffect<ActionType<typeof loadDependenciesAsync.success>>
  | PutEffect<ActionType<typeof loadDependenciesAsync.failure>>;

function* loadDependenciesRequest(
  action: ReturnType<typeof loadDependenciesAsync.request>
): Generator<LoadDependenciesRequestGeneratorType, void, boolean> {
  try {
    console.debug('loadDependenciesRequest');

    const areAcoommodationsLoaded = yield select(getAreAccommodationsLoaded);
    if (!areAcoommodationsLoaded) {
      yield put(loadAccommodationsAsync.request());
    }

    const areItemsLoaded: boolean = yield select(getAreItemsLoaded);
    if (!areItemsLoaded) {
      yield put(getItemsAsync.request());
    }

    yield put(loadDependenciesAsync.success());
  } catch (error) {
    yield put(loadDependenciesAsync.failure(error));
  }
}
